/**
 * ClientComparison — Side-by-side comparison of 2-3 clients.
 * Shows strength score, volume trend, adherence, PRs.
 */
import { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  ScrollView, ActivityIndicator,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { X, CheckSquare, Square, BarChart2 } from 'lucide-react-native';

interface ClientOption {
  id: string;
  name: string;
  strength_score: number;
}

interface ComparisonData {
  id: string;
  name: string;
  strengthScore: number;
  volumeLast30: number;
  workoutsLast30: number;
  adherencePct: number;
  prsLast30: number;
}

interface Props {
  visible: boolean;
  clients: ClientOption[];
  onClose: () => void;
}

export default function ClientComparison({ visible, clients, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [comparing, setComparing] = useState(false);
  const [data, setData] = useState<ComparisonData[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (visible) {
      setSelected(new Set());
      setData([]);
      setShowResults(false);
    }
  }, [visible]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 3) next.add(id);
      return next;
    });
  };

  const runComparison = async () => {
    if (selected.size < 2) return;
    setComparing(true);
    try {
      const ids = Array.from(selected);
      const d30 = new Date();
      d30.setDate(d30.getDate() - 30);
      const isoStart = d30.toISOString();

      const results: ComparisonData[] = await Promise.all(
        ids.map(async (id) => {
          const cl = clients.find((x) => x.id === id)!;

          const { count: workouts } = await supabase
            .from('workouts')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', id)
            .gte('performed_at', isoStart);

          const { data: sets } = await supabase
            .from('workout_sets')
            .select('weight, reps')
            .eq('user_id', id)
            .gte('performed_at', isoStart);

          const volume = (sets || []).reduce(
            (sum: number, s: any) => sum + (s.weight ?? 0) * (s.reps ?? 0),
            0,
          );

          const { count: prs } = await supabase
            .from('strength_events')
            .select('id', { count: 'exact', head: true })
            .eq('profile_id', id)
            .eq('is_pr', true)
            .gte('created_at', isoStart);

          const targetSessions = Math.round(4.3 * 3);
          const adherencePct = targetSessions > 0
            ? Math.min(100, Math.round(((workouts ?? 0) / targetSessions) * 100))
            : 0;

          return {
            id,
            name: cl.name,
            strengthScore: cl.strength_score,
            volumeLast30: volume,
            workoutsLast30: workouts ?? 0,
            adherencePct,
            prsLast30: prs ?? 0,
          };
        }),
      );

      setData(results);
      setShowResults(true);
    } finally {
      setComparing(false);
    }
  };

  const maxVolume = Math.max(...data.map((d) => d.volumeLast30), 1);

  const metricRow = (label: string, getValue: (d: ComparisonData) => string | number) => (
    <View style={styles.metricRow} key={label}>
      <Text style={[styles.metricLabel, { color: c.textSecondary }]}>{label}</Text>
      <View style={styles.metricValues}>
        {data.map((d) => (
          <Text key={d.id} style={[styles.metricValue, { color: c.text }]}>
            {getValue(d)}
          </Text>
        ))}
      </View>
    </View>
  );

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]}>COMPARE CLIENTS</Text>
            <TouchableOpacity onPress={onClose} hitSlop={8}>
              <X size={22} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {!showResults ? (
            <>
              <Text style={[styles.subtitle, { color: c.textSecondary }]}>
                Select 2-3 clients ({selected.size}/3)
              </Text>
              <ScrollView style={styles.clientList}>
                {clients.map((cl) => {
                  const isSelected = selected.has(cl.id);
                  return (
                    <TouchableOpacity
                      key={cl.id}
                      style={[styles.clientRow, { borderColor: c.border }]}
                      onPress={() => toggle(cl.id)}
                      activeOpacity={0.7}
                    >
                      {isSelected
                        ? <CheckSquare size={18} color={c.accent} />
                        : <Square size={18} color={c.textTertiary} />
                      }
                      <Text style={[styles.clientName, { color: c.text }]}>{cl.name}</Text>
                      <Text style={[styles.clientScore, { color: c.textSecondary }]}>
                        STR {cl.strength_score}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
              <TouchableOpacity
                style={[
                  styles.compareBtn,
                  { backgroundColor: selected.size >= 2 ? c.accent : c.border },
                ]}
                onPress={runComparison}
                disabled={comparing || selected.size < 2}
              >
                {comparing ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.compareBtnText}>Compare</Text>
                )}
              </TouchableOpacity>
            </>
          ) : (
            <ScrollView>
              {/* Client name headers */}
              <View style={styles.metricRow}>
                <View style={styles.metricLabelSpace} />
                <View style={styles.metricValues}>
                  {data.map((d) => (
                    <Text
                      key={d.id}
                      style={[styles.clientHeader, { color: c.accent }]}
                      numberOfLines={1}
                    >
                      {d.name}
                    </Text>
                  ))}
                </View>
              </View>

              <View style={[styles.divider, { backgroundColor: c.border }]} />

              {metricRow('Strength', (d) => d.strengthScore)}
              {metricRow('Volume (30d)', (d) => `${(d.volumeLast30 / 1000).toFixed(1)}k`)}
              {metricRow('Sessions', (d) => d.workoutsLast30)}
              {metricRow('Adherence', (d) => `${d.adherencePct}%`)}
              {metricRow('PRs', (d) => d.prsLast30)}

              {/* Volume bar chart */}
              <View style={[styles.divider, { backgroundColor: c.border }]} />
              <Text style={[styles.chartLabel, { color: c.textSecondary }]}>VOLUME COMPARISON</Text>
              {data.map((d) => (
                <View key={d.id} style={styles.barRow}>
                  <Text style={[styles.barLabel, { color: c.textSecondary }]} numberOfLines={1}>
                    {d.name}
                  </Text>
                  <View style={[styles.barTrack, { backgroundColor: c.border }]}>
                    <View
                      style={[
                        styles.barFill,
                        {
                          backgroundColor: c.accent,
                          width: `${Math.round((d.volumeLast30 / maxVolume) * 100)}%`,
                        },
                      ]}
                    />
                  </View>
                </View>
              ))}

              <TouchableOpacity
                style={[styles.compareBtn, { backgroundColor: c.accent, marginTop: 16 }]}
                onPress={() => setShowResults(false)}
              >
                <Text style={styles.compareBtnText}>Back to Selection</Text>
              </TouchableOpacity>
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  modal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    padding: 20,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
  },
  clientList: {
    maxHeight: 250,
  },
  clientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  clientName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  clientScore: {
    fontSize: 12,
    fontWeight: '700',
  },
  compareBtn: {
    borderRadius: 2,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 12,
  },
  compareBtnText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
  metricRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  metricLabel: {
    width: 100,
    fontSize: 12,
    fontWeight: '700',
  },
  metricLabelSpace: {
    width: 100,
  },
  metricValues: {
    flex: 1,
    flexDirection: 'row',
  },
  metricValue: {
    flex: 1,
    fontSize: 14,
    fontWeight: '800',
    textAlign: 'center',
  },
  clientHeader: {
    flex: 1,
    fontSize: 13,
    fontWeight: '800',
    textAlign: 'center',
  },
  divider: {
    height: 1,
    marginVertical: 8,
  },
  chartLabel: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
    marginBottom: 8,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  barLabel: {
    width: 80,
    fontSize: 12,
    fontWeight: '600',
  },
  barTrack: {
    flex: 1,
    height: 16,
    borderRadius: 4,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
  },
});
